import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InsightsIcon from '@mui/icons-material/Insights';
import AssessmentIcon from '@mui/icons-material/SpaceDashboard';

// The top-level sections, shared by the desktop header tabs and the phone
// tab bar so the two can never disagree about what a user may open. Pure:
// no hooks here (useNavSections.ts reads the current user).
export type NavSectionId = 'markets' | 'monitor' | 'portfolio';

export interface NavSection {
    id: NavSectionId;
    label: string;
    to: string;
    icon: ComponentType<SvgIconProps>;
}

// '/' for Markets: DefaultLanding forwards it to /markets.
const MARKETS: NavSection = {
    id: 'markets',
    label: 'Markets',
    to: '/',
    icon: ShowChartIcon,
};
const MONITOR: NavSection = {
    id: 'monitor',
    label: 'Monitor',
    to: '/monitor',
    icon: InsightsIcon,
};
const PORTFOLIO: NavSection = {
    id: 'portfolio',
    label: 'Portfolio',
    to: '/portfolio',
    icon: AssessmentIcon,
};

export function navSectionsFor(features: readonly string[]): NavSection[] {
    const sections: NavSection[] = [];
    // The Markets section covers both halves of the old `markets` feature.
    if (features.includes('assets') || features.includes('events'))
        sections.push(MARKETS);
    if (features.includes('monitor')) sections.push(MONITOR);
    if (features.includes('portfolio')) sections.push(PORTFOLIO);
    return sections;
}

export function activeSectionId(
    sections: readonly NavSection[],
    pathname: string
): NavSectionId | null {
    const has = (id: NavSectionId) => sections.some(s => s.id === id);
    // '/' is only a redirect, so Markets must follow the /markets subtree or
    // it never shows as selected.
    if (
        (pathname === '/' || pathname.startsWith('/markets')) &&
        has('markets')
    ) {
        return 'markets';
    }
    if (pathname.startsWith('/monitor') && has('monitor')) return 'monitor';
    if (pathname.startsWith('/portfolio') && has('portfolio'))
        return 'portfolio';
    return null;
}
